import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState, formatMoney } from "../components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Trash2, Percent, Download, Upload, Plus } from "lucide-react";
import { toast } from "sonner";

const ORDER_STATUSES = ["open", "in_progress", "completed", "cancelled"];
const ITEM_STATUSES = ["pending", "ordered", "shipped", "delivered", "shipped_to_customer", "cancelled"];
const ITEM_STATUS_FILTERS = ["all", ...ITEM_STATUSES];
const CURRENCIES = ["USD", "EUR", "TRY", "GBP", "JPY", "CNY"];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selected, setSelected] = useState({});
  const [bulkStatus, setBulkStatus] = useState("");
  const [priceOpen, setPriceOpen] = useState(false);
  const [pForm, setPForm] = useState({ target: "primary", mode: "rate", rate_percent: "", fixed_price: "", currency: "" });
  const [itemStatusFilter, setItemStatusFilter] = useState("all");
  const [itemSupplierFilter, setItemSupplierFilter] = useState("all");
  const [itemSearch, setItemSearch] = useState("");
  const [itemImportResult, setItemImportResult] = useState(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    part_number: "", manufacturer: "", quantity: "", unit_price: "",
    currency: "USD", lead_time: "", status: "pending",
  });
  const _visibleItemsRef = useRef([]);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (status) => {
    await api.put(`/orders/${id}/status`, { status });
    toast.success("Order status updated");
    load();
  };

  const updateItem = async (item_id, patch) => {
    await api.put(`/orders/${id}/items/${item_id}`, patch);
    load();
  };

  const toggle = (iid) => setSelected((s) => ({ ...s, [iid]: !s[iid] }));
  const toggleAll = () => {
    if (!order) return;
    const target = _visibleItemsRef.current;
    if (!target || target.length === 0) return;
    const all = target.every((it) => selected[it.id]);
    if (all) {
      // deselect only the currently-visible ones
      setSelected((s) => {
        const next = { ...s };
        for (const it of target) delete next[it.id];
        return next;
      });
    } else {
      setSelected((s) => {
        const next = { ...s };
        for (const it of target) next[it.id] = true;
        return next;
      });
    }
  };
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const selCount = selectedIds.length;

  const applyBulkStatus = async () => {
    if (!bulkStatus) return toast.error("Choose a status");
    if (!selCount) return toast.error("Select items first");
    try {
      const { data } = await api.put(`/orders/${id}/items/bulk-status`, { item_ids: selectedIds, status: bulkStatus });
      const n = data.updated || selCount;
      toast.success(`${n} item${n === 1 ? "" : "s"} updated`);
      setSelected({}); setBulkStatus("");
      load();
    } catch { toast.error("Bulk update failed"); }
  };

  const applyBulkPrice = async (e) => {
    e.preventDefault();
    if (!selCount) return toast.error("Select items first");
    const body = { item_ids: selectedIds, target: pForm.target, mode: pForm.mode, currency: pForm.currency || null };
    if (pForm.mode === "rate") {
      const r = parseFloat(pForm.rate_percent);
      if (isNaN(r)) return toast.error("Enter a rate %");
      body.rate_percent = r;
    } else {
      const f = parseFloat(pForm.fixed_price);
      if (isNaN(f)) return toast.error("Enter a fixed price");
      body.fixed_price = f;
    }
    try {
      const { data } = await api.put(`/orders/${id}/items/bulk-purchase`, body);
      const n = data.updated || 0;
      const sk = data.skipped || 0;
      toast.success(`${n} item${n === 1 ? "" : "s"} priced${sk ? ` · ${sk} skipped (no sub-supplier price)` : ""}`);
      setPriceOpen(false); setSelected({});
      setPForm({ target: "primary", mode: "rate", rate_percent: "", fixed_price: "", currency: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteOrder = async () => {
    if (!order) return;
    if (!window.confirm(`Delete order ${order.order_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      navigate("/orders");
    } catch { toast.error("Failed to delete"); }
  };

  const exportItemsXlsx = async () => {
    try {
      const res = await api.get(`/orders/${id}/items.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `${order?.order_number || "order"}-items-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Items exported");
    } catch { toast.error("Export failed"); }
  };

  const importItemsXlsx = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/orders/${id}/items/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItemImportResult({ ...data, filename: file.name });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
  };

  const submitAddItem = async (e) => {
    e.preventDefault();
    const qty = parseFloat(addForm.quantity);
    const price = parseFloat(addForm.unit_price);
    if (!addForm.part_number.trim()) return toast.error("Part number is required");
    if (isNaN(qty) || qty <= 0) return toast.error("Quantity must be > 0");
    if (isNaN(price) || price < 0) return toast.error("Unit price must be ≥ 0");
    try {
      await api.post(`/orders/${id}/items`, {
        part_number: addForm.part_number.trim(),
        manufacturer: addForm.manufacturer.trim() || null,
        quantity: qty,
        unit_price: price,
        currency: addForm.currency,
        lead_time: addForm.lead_time.trim() || null,
        status: addForm.status,
      });
      toast.success(`Line item ${addForm.part_number} added`);
      setAddItemOpen(false);
      setAddForm({ part_number: "", manufacturer: "", quantity: "", unit_price: "", currency: "USD", lead_time: "", status: "pending" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add item");
    }
  };

  if (!order) return <PageBody>Loading…</PageBody>;
  const suppliers = order.suppliers || [];

  // Filter items by status + search (part #, mfr) — filter is view-only,
  // bulk actions still operate on all currently-selected ids.
  const searchLc = itemSearch.trim().toLowerCase();
  const visibleItems = order.items.filter((it) => {
    if (itemStatusFilter !== "all" && (it.status || "pending") !== itemStatusFilter) return false;
    if (itemSupplierFilter !== "all" && it.supplier_id !== itemSupplierFilter && it.sub_supplier_id !== itemSupplierFilter) return false;
    if (searchLc && !(
      (it.part_number || "").toLowerCase().includes(searchLc) ||
      (it.manufacturer || "").toLowerCase().includes(searchLc)
    )) return false;
    return true;
  });
  const allSelected = visibleItems.length > 0 && visibleItems.every((it) => selected[it.id]);
  _visibleItemsRef.current = visibleItems;

  const statusCounts = order.items.reduce((acc, it) => {
    const k = it.status || "pending";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  // Suppliers actually referenced by this order (primary or sub) with per-supplier counts
  const supIdCounts = {};
  for (const it of order.items) {
    if (it.supplier_id) supIdCounts[it.supplier_id] = (supIdCounts[it.supplier_id] || 0) + 1;
    if (it.sub_supplier_id && it.sub_supplier_id !== it.supplier_id) {
      supIdCounts[it.sub_supplier_id] = (supIdCounts[it.sub_supplier_id] || 0) + 1;
    }
  }
  const orderSuppliers = (order.suppliers || []).filter((s) => supIdCounts[s.id]);

  // supplier lookup for showing rate hint in dialog
  const primarySupIds = new Set(order.items.filter((it) => selectedIds.includes(it.id)).map((it) => it.supplier_id).filter(Boolean));
  const subSupIds = new Set(order.items.filter((it) => selectedIds.includes(it.id)).map((it) => it.sub_supplier_id).filter(Boolean));
  const hintSupIds = pForm.target === "primary" ? primarySupIds : subSupIds;
  const hintRates = Array.from(hintSupIds).map((sid) => suppliers.find((s) => s.id === sid)).filter((s) => s && s.default_rate_percent != null);

  const supplierOptions = suppliers.map((s) => ({ id: s.id, name: s.name, rate: s.default_rate_percent }));

  return (
    <>
      <PageHeader
        title={<span className="font-mono">{order.order_number}</span>}
        subtitle={<><Link to="/orders" className="hover:text-blue-700">Orders</Link> · {order.customer?.name}</>}
        testid="order-detail-title"
        actions={
          <div className="flex items-center gap-3">
            <span className="label-caps">Order status</span>
            <Select value={order.status} onValueChange={updateStatus}>
              <SelectTrigger className="w-40 rounded-none" data-testid="order-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              onClick={exportItemsXlsx}
              variant="outline"
              className="rounded-none border-slate-300 hover:bg-slate-100"
              data-testid="export-items-btn"
              title="Download Excel of line items for bulk editing"
            >
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <label
              className="inline-flex items-center rounded-none border border-slate-300 bg-white hover:bg-slate-100 px-3 h-9 text-sm cursor-pointer"
              data-testid="import-items-label"
              title="Upload edited Excel to update purchase prices & suppliers"
            >
              <Upload className="w-4 h-4 mr-2" /> Import
              <input
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={importItemsXlsx}
                data-testid="import-items-input"
              />
            </label>
            <Button
              onClick={() => setAddItemOpen(true)}
              variant="outline"
              className="rounded-none border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="add-item-btn"
              title="Add a new line item to this order"
            >
              <Plus className="w-4 h-4 mr-2" /> Add item
            </Button>
            <Button
              onClick={deleteOrder}
              variant="outline"
              className="rounded-none border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="delete-order-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        }
      />
      <PageBody>
        <div className="grid md:grid-cols-4 border border-slate-300 bg-white mb-6">
          {[
            ["From Offer", order.offer_number || "-"],
            ["Customer", order.customer?.name || "-"],
            ["Items", order.items.length],
            ["Status", <StatusBadge key="s" status={order.status} />],
          ].map(([k, v], i, a) => (
            <div key={k} className={`p-5 ${i < a.length - 1 ? "border-r border-slate-200" : ""}`}>
              <div className="label-caps">{k}</div><div className="mt-2 font-mono text-slate-900">{v}</div>
            </div>
          ))}
        </div>

        <div className="mb-3 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="label-caps">Line items</div>
            <h2 className="font-display text-2xl tracking-tight text-slate-900">Assign suppliers · Enter net purchase prices</h2>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="label-caps text-[10px]">Search</Label>
              <Input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder="Part #, mfr…"
                className="rounded-none mt-1 h-9 w-44 text-xs"
                data-testid="order-item-search"
              />
            </div>
            <div>
              <Label className="label-caps text-[10px]">Item status</Label>
              <Select value={itemStatusFilter} onValueChange={setItemStatusFilter}>
                <SelectTrigger className="rounded-none mt-1 h-9 w-44 text-xs" data-testid="order-item-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {ITEM_STATUS_FILTERS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`order-item-status-filter-${s}`}>
                      {s === "all" ? `All (${order.items.length})` : `${s.replace(/_/g, " ")} (${statusCounts[s] || 0})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-[10px]">Supplier</Label>
              <Select value={itemSupplierFilter} onValueChange={setItemSupplierFilter}>
                <SelectTrigger className="rounded-none mt-1 h-9 w-48 text-xs" data-testid="order-item-supplier-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all" data-testid="order-item-supplier-filter-all">All suppliers ({order.items.length})</SelectItem>
                  {orderSuppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`order-item-supplier-filter-${s.id}`}>
                      {s.name} ({supIdCounts[s.id]})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="mb-3 flex items-end justify-end gap-2">
          <div className={`flex items-center gap-2 transition-opacity duration-200 ${selCount ? "opacity-100" : "opacity-40 pointer-events-none"}`}
               data-testid="bulk-action-bar">
            <span className="label-caps" data-testid="bulk-selected-count">{selCount} selected</span>
            <Dialog open={priceOpen} onOpenChange={setPriceOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-price-btn"><Percent className="w-4 h-4 mr-2" /> Bulk purchase prices</Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-md">
                <DialogHeader><DialogTitle className="font-display tracking-tight">Bulk purchase prices ({selCount})</DialogTitle></DialogHeader>
                <form onSubmit={applyBulkPrice} className="space-y-3">
                  <div>
                    <Label className="label-caps">Target</Label>
                    <Select value={pForm.target} onValueChange={(v) => setPForm({ ...pForm, target: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="bulk-price-target"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="primary">Primary supplier price</SelectItem>
                        <SelectItem value="sub">Sub-supplier price</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-caps">Mode</Label>
                    <Select value={pForm.mode} onValueChange={(v) => setPForm({ ...pForm, mode: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="bulk-price-mode"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="rate">Apply rate % {pForm.target === "primary" ? "(primary = sub × (1 + rate))" : "(sub = sale × (1 − rate))"}</SelectItem>
                        <SelectItem value="fixed">Fixed price for all rows</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {pForm.mode === "rate" ? (
                    <div>
                      <Label className="label-caps">Rate %</Label>
                      <Input type="number" step="any" value={pForm.rate_percent} onChange={(e) => setPForm({ ...pForm, rate_percent: e.target.value })} placeholder={pForm.target === "primary" ? "e.g. 6 = +6% markup over sub price" : "e.g. 6 = 6% off sale"} className="rounded-none mt-1" data-testid="bulk-price-rate" />
                      {hintRates.length > 0 && (
                        <div className="text-xs text-slate-500 mt-1">
                          Default rates: {hintRates.map((s) => `${s.name} ${s.default_rate_percent}%`).join(" · ")}
                        </div>
                      )}
                      {pForm.target === "primary" && (
                        <div className="text-xs text-slate-500 mt-1">Rows without a sub-supplier price will be skipped.</div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Label className="label-caps">Fixed price</Label>
                      <Input type="number" step="any" value={pForm.fixed_price} onChange={(e) => setPForm({ ...pForm, fixed_price: e.target.value })} className="rounded-none mt-1" data-testid="bulk-price-fixed" />
                    </div>
                  )}
                  <div>
                    <Label className="label-caps">Currency (blank = use item's sale currency)</Label>
                    <Select value={pForm.currency || "auto"} onValueChange={(v) => setPForm({ ...pForm, currency: v === "auto" ? "" : v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="bulk-price-currency"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="auto">Auto (same as sale)</SelectItem>
                        {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter><Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="bulk-price-apply">Apply</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="w-40 rounded-none h-9" data-testid="bulk-status-select"><SelectValue placeholder="Change status to…" /></SelectTrigger>
              <SelectContent className="rounded-none">
                {ITEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={applyBulkStatus} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" data-testid="bulk-apply-btn">Apply status</Button>
          </div>
        </div>

        {order.items.length === 0 ? <EmptyState title="No items" /> :
          visibleItems.length === 0 ? <EmptyState title="No matching items" hint="Adjust the search or item-status filter above." /> :
          <div className="bg-white border border-slate-300 overflow-x-auto" data-testid="order-items-table-wrapper">
            <div className="px-3 py-2 text-xs text-slate-500 border-b border-slate-200 flex items-center justify-between">
              <span data-testid="order-items-visible-count">
                Showing <span className="font-mono text-slate-800">{visibleItems.length}</span> of <span className="font-mono text-slate-800">{order.items.length}</span> line item{order.items.length === 1 ? "" : "s"}
              </span>
              {(itemStatusFilter !== "all" || itemSupplierFilter !== "all" || itemSearch) && (
                <button
                  onClick={() => { setItemStatusFilter("all"); setItemSupplierFilter("all"); setItemSearch(""); }}
                  className="text-blue-700 hover:text-blue-900 font-mono uppercase tracking-wider text-[10px]"
                  data-testid="clear-item-filters"
                >
                  Clear filters
                </button>
              )}
            </div>
            <table className="w-full text-sm table-fixed [&_td]:px-2 [&_th]:px-2">
              <colgroup>
                <col style={{ width: "30px" }} />   {/* checkbox */}
                <col style={{ width: "100px" }} />  {/* PART # */}
                <col style={{ width: "52px" }} />   {/* QTY */}
                <col style={{ width: "62px" }} />   {/* SALE */}
                <col style={{ width: "80px" }} />   {/* LEAD */}
                <col style={{ width: "110px" }} />  {/* PRIMARY SUPPLIER */}
                <col style={{ width: "108px" }} />  {/* PRIMARY COST */}
                <col style={{ width: "110px" }} />  {/* SUB-SUPPLIER */}
                <col style={{ width: "108px" }} />  {/* SUB COST */}
                <col style={{ width: "48px" }} />   {/* RE-REEL */}
                <col style={{ width: "150px" }} />  {/* STATUS */}
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-order-items" />
                </th>
                <th className="py-2 px-3 label-caps">Part #</th>
                <th className="py-2 px-3 label-caps text-right">Qty</th>
                <th className="py-2 px-3 label-caps text-right">Sale</th>
                <th className="py-2 px-3 label-caps">Lead</th>
                <th className="py-2 px-3 label-caps">Primary Supplier</th>
                <th className="py-2 px-3 label-caps text-right">Primary Cost</th>
                <th className="py-2 px-3 label-caps">Sub-supplier</th>
                <th className="py-2 px-3 label-caps text-right">Sub Cost</th>
                <th className="py-2 px-3 label-caps text-center" title="Re-reel required (adds sub-supplier's flat re-reel fee to cost before primary markup)">Re-reel</th>
                <th className="py-2 px-3 label-caps">Status</th>
              </tr></thead>
              <tbody>
                {visibleItems.map((it) => (
                  <tr key={it.id} className={`border-b border-slate-200 hover:bg-slate-50 stagger-row ${selected[it.id] ? "bg-blue-50/40" : ""}`} data-testid={`order-item-row-${it.id}`}>
                    <td className="py-2 px-3">
                      <Checkbox checked={!!selected[it.id]} onCheckedChange={() => toggle(it.id)} data-testid={`select-order-item-${it.id}`} />
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-mono font-medium">{it.part_number}</div>
                      <div className="text-xs text-slate-500">{it.manufacturer || ""}</div>
                    </td>
                    <td className="py-2 px-3 font-mono text-right">{it.quantity}</td>
                    <td className="py-2 px-3 font-mono text-right">{formatMoney(it.unit_price, it.currency, 4)}</td>
                    <td className="py-2 px-3">
                      <input
                        defaultValue={it.lead_time || ""}
                        onBlur={(e) => { if (e.target.value !== (it.lead_time || "")) updateItem(it.id, { lead_time: e.target.value || null }); }}
                        placeholder="—"
                        className="w-full h-8 rounded-none border border-slate-300 px-2 text-xs bg-white"
                        data-testid={`order-item-lead-time-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Select value={it.supplier_id || "none"} onValueChange={(v) => updateItem(it.id, { supplier_id: v === "none" ? null : v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs w-full" data-testid={`order-item-supplier-${it.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="none">— none —</SelectItem>
                          {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.default_rate_percent != null ? ` (${s.default_rate_percent}%)` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 justify-end">
                        <input
                          type="number" step="any"
                          defaultValue={it.purchase_price ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value === "" ? null : parseFloat(e.target.value);
                            if (v !== it.purchase_price) updateItem(it.id, { purchase_price: v, purchase_currency: it.purchase_currency || it.currency });
                          }}
                          className="w-full min-w-0 h-8 rounded-none border border-slate-300 px-2 text-right font-mono text-xs bg-white"
                          data-testid={`order-item-purchase-${it.id}`}
                        />
                        <span className="text-xs font-mono text-slate-500">{it.purchase_currency || it.currency}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <Select value={it.sub_supplier_id || "none"} onValueChange={(v) => updateItem(it.id, { sub_supplier_id: v === "none" ? null : v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs w-full" data-testid={`order-item-sub-supplier-${it.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          <SelectItem value="none">— none —</SelectItem>
                          {supplierOptions.filter((s) => s.id !== it.supplier_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.rate != null ? ` (${s.rate}%)` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 justify-end">
                        <input
                          type="number" step="any"
                          defaultValue={it.sub_purchase_price ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value === "" ? null : parseFloat(e.target.value);
                            if (v !== it.sub_purchase_price) updateItem(it.id, { sub_purchase_price: v, sub_purchase_currency: it.sub_purchase_currency || it.currency });
                          }}
                          className="w-full min-w-0 h-8 rounded-none border border-slate-300 px-2 text-right font-mono text-xs bg-white"
                          data-testid={`order-item-sub-purchase-${it.id}`}
                        />
                        <span className="text-xs font-mono text-slate-500">{it.sub_purchase_currency || it.currency}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Checkbox
                        checked={!!it.re_reel}
                        onCheckedChange={(v) => updateItem(it.id, { re_reel: !!v })}
                        data-testid={`order-item-re-reel-${it.id}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <Select value={it.status || "pending"} onValueChange={(v) => updateItem(it.id, { status: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs w-full" data-testid={`order-item-status-${it.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="rounded-none">
                          {ITEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>
      <ItemImportResultDialog result={itemImportResult} onClose={() => setItemImportResult(null)} />
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="add-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-700" /> Add line item
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAddItem} className="space-y-3">
            <div>
              <Label className="label-caps">Part number *</Label>
              <Input
                value={addForm.part_number}
                onChange={(e) => setAddForm({ ...addForm, part_number: e.target.value })}
                placeholder="e.g. GRM188Z71A475ME15D"
                className="rounded-none mt-1 font-mono"
                data-testid="add-item-part-number"
                autoFocus
              />
            </div>
            <div>
              <Label className="label-caps">Manufacturer</Label>
              <Input
                value={addForm.manufacturer}
                onChange={(e) => setAddForm({ ...addForm, manufacturer: e.target.value })}
                placeholder="e.g. Murata Electronics"
                className="rounded-none mt-1"
                data-testid="add-item-manufacturer"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="label-caps">Quantity *</Label>
                <Input
                  type="number" step="any"
                  value={addForm.quantity}
                  onChange={(e) => setAddForm({ ...addForm, quantity: e.target.value })}
                  className="rounded-none mt-1 font-mono"
                  data-testid="add-item-quantity"
                />
              </div>
              <div>
                <Label className="label-caps">Sale price *</Label>
                <Input
                  type="number" step="any"
                  value={addForm.unit_price}
                  onChange={(e) => setAddForm({ ...addForm, unit_price: e.target.value })}
                  className="rounded-none mt-1 font-mono"
                  data-testid="add-item-unit-price"
                />
              </div>
              <div>
                <Label className="label-caps">Currency</Label>
                <Select value={addForm.currency} onValueChange={(v) => setAddForm({ ...addForm, currency: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-item-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-caps">Lead time</Label>
                <Input
                  value={addForm.lead_time}
                  onChange={(e) => setAddForm({ ...addForm, lead_time: e.target.value })}
                  placeholder="e.g. 3 weeks"
                  className="rounded-none mt-1"
                  data-testid="add-item-lead-time"
                />
              </div>
              <div>
                <Label className="label-caps">Status</Label>
                <Select value={addForm.status} onValueChange={(v) => setAddForm({ ...addForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="add-item-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {ITEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-xs text-slate-500 pt-2">
              You can assign primary/sub suppliers and enter purchase prices after the item is created, directly from the line-items table.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddItemOpen(false)} className="rounded-none" data-testid="add-item-cancel">Cancel</Button>
              <Button type="submit" className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-item-submit">
                <Plus className="w-4 h-4 mr-2" /> Add item
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ItemImportResultDialog({ result, onClose }) {
  if (!result) return null;
  const { filename, updated = 0, added = 0, skipped = 0, errors = [] } = result;
  const hasErrors = errors.length > 0;
  return (
    <Dialog open={!!result} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="item-import-result">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {(updated + added) > 0 && !hasErrors ? "Line items imported" : hasErrors ? "Imported with warnings" : "No changes applied"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {filename && (
            <div className="text-xs text-slate-500 truncate">
              File: <span className="font-mono text-slate-700">{filename}</span>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 border border-slate-300 bg-white">
              <div className="label-caps text-slate-500 text-[10px]">Rows updated</div>
              <div className={`mt-1 font-mono text-2xl tracking-tight ${updated > 0 ? "text-emerald-700" : "text-slate-900"}`} data-testid="item-import-updated">{updated}</div>
            </div>
            <div className="p-3 border border-slate-300 bg-white">
              <div className="label-caps text-slate-500 text-[10px]">Rows added</div>
              <div className={`mt-1 font-mono text-2xl tracking-tight ${added > 0 ? "text-blue-700" : "text-slate-900"}`} data-testid="item-import-added">{added}</div>
            </div>
            <div className="p-3 border border-slate-300 bg-white">
              <div className="label-caps text-slate-500 text-[10px]">Rows skipped</div>
              <div className={`mt-1 font-mono text-2xl tracking-tight ${skipped > 0 ? "text-amber-700" : "text-slate-900"}`} data-testid="item-import-skipped">{skipped}</div>
            </div>
          </div>
          {hasErrors && (
            <div>
              <div className="label-caps mb-2 text-amber-800">Row issues</div>
              <div className="border border-amber-200 bg-amber-50 max-h-72 overflow-y-auto" data-testid="item-import-errors">
                <ul className="divide-y divide-amber-200">
                  {errors.map((err, i) => (
                    <li key={i} className="px-3 py-2 text-xs font-mono text-amber-900">{err}</li>
                  ))}
                </ul>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Tip: rows are matched by <span className="font-mono">Item ID</span>. Rows with an empty or unknown Item ID become new line items — they need at least Part Number, Quantity and Sale Price.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-none bg-slate-800 hover:bg-slate-900 text-white" data-testid="item-import-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
